import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from './ErrorBoundary';

interface PageContainerProps {
    children: ReactNode;
    title?: string;
    description?: string;
    actions?: ReactNode;
    className?: string;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const MAX_WIDTHS = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    full: 'max-w-full',
};

export function PageContainer({
    children,
    title,
    description,
    actions,
    className,
    maxWidth = 'xl',
}: PageContainerProps) {
    return (
        <ErrorBoundary level="page">
            <div className={cn(
                'px-4 sm:px-6 lg:px-8 py-6 mx-auto w-full',
                MAX_WIDTHS[maxWidth],
                className
            )}>
                {(title || actions) && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div>
                            {title && <h1 className="text-2xl font-bold tracking-tight">{title}</h1>}
                            {description && (
                                <p className="text-muted-foreground mt-1">{description}</p>
                            )}
                        </div>
                        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
                    </div>
                )}
                {children}
            </div>
        </ErrorBoundary>
    );
}